import React, { FC, useRef, useEffect, useState } from "react";
import { Menu, Dropdown, Skeleton } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import { toJpeg, toCanvas } from "html-to-image";
import { jsPDF } from "jspdf";
import { CSVLink } from "react-csv";

import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  ResponsiveContainer
} from "recharts";

import {
  ItemWrapper,
  Title,
  DropdownWrapper,
  GraphTitle,
  List,
  ListValue,
  GraphListContainer,
  Graph,
  GraphText,
  ListValueApplicants
} from "./elements";

type Props = {
  isLoss: boolean;
  data: Array<{
    name: string;
    value: number;
    barColor: string;
    percentage: number;
  }>;
  onClick?: (e: any) => void | (() => void);
  isLoading?: boolean;
  dateRange?: any;
};

const BarGraphInsight: FC<Props> = props => {
  const { isLoss, data, onClick, isLoading, dateRange } = props;
  const csvRef = useRef<any>();
  const jpgRef = useRef<any>();
  const [csvData, setCsvData] = useState<Array<any>>([]);

  const filteredDate = dateRange
    ? `${dateRange[0].format("YYYY-MM-DD")} - ${dateRange[1].format(
        "YYYY-MM-DD"
      )}`
    : "";

  useEffect(() => {
    if (data.length) {
      const items = data.reduce((arr: any, item): any => {
        arr.push({
          firstRow: item.name,
          secondRow: `${item.percentage}%`,
          thirdRow: item.value
        });
        return arr;
      }, []);

      setCsvData(items);
    }
  }, [data]);

  const handleClickMenu = (e: any) => {
    if (onClick) {
      onClick(e);
    } else {
      switch (e.key) {
        case "CSV":
          csvRef.current?.link.click();
          break;
        case "JPG":
          toJpeg(jpgRef.current, { cacheBust: true })
            .then(url => {
              const link = document.createElement("a");
              link.download = isLoss
                ? "LostApplicants.jpg"
                : "ApplicationSources.jpg";
              link.href = url;
              link.click();
            })
            .catch(err => {
              console.log(err);
            });
          break;
        default:
          toCanvas(jpgRef.current).then(canvas => {
            const image = canvas.toDataURL("image/jpeg", 1.0);
            const doc = new jsPDF("p", "px", "a4");
            doc.addImage(image, "JPEG", 10, 10, 0, 0);
            doc.save(isLoss ? "lostapplicants.pdf" : "applicationsources.pdf");
          });
          break;
      }
    }
  };

  const dropdownMenu = (
    <Menu onClick={handleClickMenu}>
      <Menu.Item key="JPG">Download JPG</Menu.Item>
      <Menu.Item key="PDF">Download PDF</Menu.Item>
      <Menu.Item key="CSV">Download CSV</Menu.Item>
    </Menu>
  );

  const renderCustomizedLabel = (props: any) => {
    const { x, y, width, value } = props;

    return (
      <g>
        <GraphText
          x={x + width / 2}
          y={y + 9}
          fill="#fff"
          textAnchor="middle"
          dominantBaseline="middle"
        >
          {value ? `${value}%` : null}
        </GraphText>
      </g>
    );
  };

  const renderCustomizedXTick = (props: any) => {
    const { x, y, payload, width } = props;

    return (
      <GraphText
        x={x - (width + 10)}
        y={y - 4}
        textAnchor="start"
        dominantBaseline="hanging"
      >
        {payload.value}
      </GraphText>
    );
  };

  return (
    <ItemWrapper ref={jpgRef}>
      <Title>{isLoss ? "Lost Applicants" : "Application sources"}</Title>
      <DropdownWrapper>
        <Dropdown
          trigger={["click"]}
          overlay={dropdownMenu}
          placement="bottomLeft"
        >
          <MoreOutlined
            rotate={90}
            style={{ fontSize: "26px", color: "#000000" }}
          />
        </Dropdown>
      </DropdownWrapper>
      {isLoading ? (
        <Skeleton paragraph={{ rows: 2 }} active />
      ) : (
        <GraphListContainer>
          <Graph isLoss={isLoss}>
            <GraphTitle>% of {isLoss ? "Lost" : "Applies"}</GraphTitle>
            <ResponsiveContainer height={isLoss ? "84%" : "86%"}>
              <BarChart
                width={500}
                height={300}
                data={data}
                layout="vertical"
                margin={{ top: 0, right: 0, left: 20, bottom: 0 }}
              >
                <XAxis
                  type="number"
                  dataKey="percentage"
                  hide
                  domain={[0, 100]}
                  padding={{ left: 20 }}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={renderCustomizedXTick}
                  padding={{ top: 4.5, bottom: 4.5 }}
                  interval={0}
                />
                <Bar
                  dataKey="percentage"
                  maxBarSize={16}
                  label={renderCustomizedLabel}
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={`${entry.barColor}`} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Graph>
          <List>
            <GraphTitle isCounter={true}>
              # {isLoss ? "Lost" : "Applies"}
            </GraphTitle>
            {data.map((item, index) =>
              isLoss ? (
                <ListValue key={`cell-${index}`}>{item.value}</ListValue>
              ) : (
                <ListValueApplicants key={`cell-${index}`}>
                  {item.value}
                </ListValueApplicants>
              )
            )}
          </List>
        </GraphListContainer>
      )}

      <CSVLink
        style={{ visibility: "hidden" }}
        data={[
          { firstRow: "Date Range: ", secondRow: filteredDate, thirdRow: "" },
          {},
          {
            firstRow: isLoss ? "Status" : "Source",
            secondRow: `% of ${isLoss ? "Loss" : "Applies"}`,
            thirdRow: `Number of ${isLoss ? "Loss" : "Applies"}`
          },
          ...csvData
        ]}
        headers={[
          { key: "firstRow", label: "" },
          { key: "secondRow", label: "" },
          { key: "thirdRow", label: "" }
        ]}
        filename={isLoss ? "Lost Applicants.csv" : "Application Sources.csv"}
        ref={csvRef}
      />
    </ItemWrapper>
  );
};

export default BarGraphInsight;
